import React, { useMemo } from 'react';
import Select from 'react-select';
import { themeColors } from '../../../styles/theme';
import './PmrSelect.css';

const buildStyles = ({ isInvalid, compact, embedded }) => ({
  control: (base, state) => {
    const focused = state.isFocused;
    const borderColor = isInvalid ? '#ef4444' : focused ? themeColors.primary : '#d1d5db';
    const focusRing = isInvalid
      ? '0 0 0 0.15rem rgba(239, 68, 68, 0.25)'
      : '0 0 0 0.15rem rgba(227, 83, 54, 0.25)';

    if (embedded) {
      return {
        ...base,
        minHeight: compact ? '38px' : '42px',
        height: compact ? '38px' : '42px',
        minWidth: '4.5rem',
        width: 'auto',
        border: 'none',
        borderRight: `1px solid ${isInvalid ? '#ef4444' : '#d1d5db'}`,
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: '#f9fafb',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'transparent',
          borderRightColor: isInvalid ? '#ef4444' : '#d1d5db',
        },
      };
    }

    return {
      ...base,
      minHeight: compact ? '38px' : '40px',
      borderRadius: '6px',
      borderColor,
      boxShadow: focused ? focusRing : 'none',
      fontSize: compact ? '0.875rem' : '1rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      '&:hover': {
        borderColor,
      },
    };
  },
  valueContainer: (base) => ({
    ...base,
    padding: embedded ? '0 0 0 6px' : compact ? '0 0.65rem' : base.padding,
    flexWrap: 'nowrap',
  }),
  singleValue: (base) => ({
    ...base,
    color: themeColors.textHeading,
    fontSize: embedded ? '0.75rem' : compact ? '0.875rem' : '1rem',
    fontWeight: embedded ? 600 : 400,
    maxWidth: embedded ? 'none' : base.maxWidth,
    position: embedded ? 'static' : base.position,
    transform: embedded ? 'none' : base.transform,
    overflow: embedded ? 'visible' : base.overflow,
  }),
  placeholder: (base) => ({
    ...base,
    color: themeColors.textMuted,
    fontSize: compact ? '0.875rem' : '1rem',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected
      ? themeColors.primary
      : isFocused
        ? 'rgba(227, 83, 54, 0.12)'
        : '#fff',
    color: isSelected ? '#fff' : themeColors.text,
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 9999,
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    paddingTop: 4,
    paddingBottom: 4,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: embedded ? 0 : base.padding,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: themeColors.textMuted,
    padding: embedded ? '0 2px' : '0 0.5rem',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
});

const PmrSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  isInvalid = false,
  compact = false,
  embedded = false,
  className = '',
  isSearchable = false,
  isDisabled = false,
  name,
  'aria-label': ariaLabel,
}) => {
  const selected = useMemo(
    () => options.find((option) => String(option.value) === String(value)) ?? null,
    [options, value]
  );

  const styles = useMemo(
    () => buildStyles({ isInvalid, compact, embedded }),
    [isInvalid, compact, embedded]
  );

  return (
    <Select
      inputId={name}
      name={name}
      aria-label={ariaLabel}
      className={`pmr-select-root ${embedded ? 'pmr-select-embedded' : ''} ${className}`.trim()}
      classNamePrefix="pmr-select"
      value={selected}
      onChange={(option) => onChange(option ? option.value : '')}
      options={options}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      styles={styles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      blurInputOnSelect
      isInvalid={isInvalid}
    />
  );
};

export default PmrSelect;
